import {
  createFileRoute,
  lazyRouteComponent,
  Outlet,
  redirect,
  useMatch,
  useMatches,
  useNavigate,
} from "@tanstack/react-router"
import {
  type RefObject,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { z } from "zod"

import { loadTerminalDetectionFeedback } from "@/actions/terminal-detection-feedback-actions"
import {
  loadTerminalFeed,
  loadTerminalFileRequests,
  loadTerminalPortfolio,
  loadTerminalPortfolios,
} from "@/actions/terminal-file-actions"
import {
  normalizeTerminalGraphQuery,
  normalizeTerminalGraphTerm,
} from "@/components/marketing/terminal-graph/terminal-graph-state"
import terminalWebsiteStyles from "@/components/marketing/website/terminal-website.css?url"
import { CommandPalette } from "@/components/terminal/command-palette"
import { CompanyList, listOfOpenFile } from "@/components/terminal/company-list"
import { DetectionFeedbackProvider } from "@/components/terminal/feed/detection-feedback"
import {
  DETECTION_SPINE_WIDTH_PX,
  FeedPane,
} from "@/components/terminal/feed/feed-pane"
import { findCompanyRow } from "@/components/terminal/home/find-in-view"
import { HomeDock } from "@/components/terminal/home/home-dock"
import { companiesTargetOf } from "@/components/terminal/home/use-home-key-risk-tables"
import { ScreenGrain } from "@/components/terminal/screen-grain"
import { useJumpHighlight } from "@/components/terminal/search-palette"
import { TerminalEmptyState } from "@/components/terminal/terminal-empty-state"
import {
  TerminalHeader,
  type TerminalHomeView,
} from "@/components/terminal/terminal-header"
import type { AssessmentBand } from "@/constants/terminal/assessment-band"
import { CompanySortKey } from "@/constants/terminal/company-sort-key"
import { DetectionClass } from "@/constants/terminal/detection-class"
import { ExposureView } from "@/constants/terminal/exposure-view"
import { FeedViewMode } from "@/constants/terminal/feed-view-mode"
import { FileRequestStatus } from "@/constants/terminal/file-request-status"
import { HomeReport } from "@/constants/terminal/home-report"
import { HomeView } from "@/constants/terminal/home-view"
import { PortfolioCompanyStatus } from "@/constants/terminal/portfolio-company-status"
import { TimeWindow } from "@/constants/terminal/time-window"
import type { TerminalPortfolioOption } from "@/lib/terminal/file-view"
import { shellModeOf } from "@/lib/terminal/shell-mode"
import { useTerminalTheme } from "@/lib/terminal/use-terminal-theme"
import { cn } from "@/lib/utils"
import { useTerminalRailLayout } from "@/routes/_terminal/-use-terminal-rail-layout"
import { useTerminalHomeStore } from "@/stores/terminal-home-store"

// Roster sort, the band filter and the feed time window are session-local UI
// state held in the shell, not URL params. Not `.strict()`: stale keys from an
// old link are stripped rather than thrown.
const terminalSearchSchema = z.object({
  portfolio: z.string().optional(),
  // The anonymous website shares this route. Keep its graph state valid
  // without changing the authenticated product's URL contract.
  q: z.preprocess(normalizeTerminalGraphQuery, z.string().optional()),
  term: z.preprocess(normalizeTerminalGraphTerm, z.string().optional()),
  // Home view state: Activity or Exposure; absent with no open Report,
  // Home lands on Exposure (`shownHomeView`). `population` toggles the
  // Portfolio/Pipeline roster. `.catch(undefined)` keeps a stale value from
  // throwing in validateSearch.
  view: z.nativeEnum(HomeView).optional().catch(undefined),
  population: z.nativeEnum(PortfolioCompanyStatus).optional().catch(undefined),
  // The open Report (portfolio-screen proposal); Home shows it in place of a
  // view.
  report: z.nativeEnum(HomeReport).optional().catch(undefined),
  // Exposure's open view; absent, Exposure opens on By Market.
  exposure: z.nativeEnum(ExposureView).optional().catch(undefined),
})

const TerminalWebsitePage = lazyRouteComponent(
  () => import("@/components/marketing/website/terminal-website-page"),
  "TerminalWebsitePage"
)

// The Portfolio a user lands on when they can open more than one (an admin
// also sees the demo Portfolio); otherwise their first.
const DEFAULT_PORTFOLIO_SLUG = "enfin"
const defaultPortfolio = (portfolios: TerminalPortfolioOption[]) =>
  portfolios.find(({ slug }) => slug === DEFAULT_PORTFOLIO_SLUG) ??
  portfolios[0]

const LEFT_COLUMN_MIN_WIDTH_PX = 286
const LEFT_COLUMN_MAX_WIDTH_PX = 392
const LEFT_COLUMN_VIEWPORT_RATIO = 0.23
const COMPANY_RAIL_COLLAPSED_WIDTH_PX = 288
const DETECTION_COLUMN_MIN_WIDTH_PX = 168
const DETECTION_COLUMN_MAX_WIDTH_PX = 232
// One feed lane per Detection class.
const DETECTION_COLUMN_COUNT = Object.values(DetectionClass).length
const FILE_MIN_WIDTH_PX = 560
const FILE_WIDE_MIN_WIDTH_PX = 672
const TWO_XL_WIDTH_PX = 1536

const getCompanyRailWidth = ({
  viewportWidth,
  isHome,
  isSidebarCollapsed,
}: {
  viewportWidth: number
  isHome: boolean
  isSidebarCollapsed: boolean
}) => {
  if (!isHome && isSidebarCollapsed) return COMPANY_RAIL_COLLAPSED_WIDTH_PX
  // The Portfolio rail and the Companies list share one width, so switching
  // modes moves nothing but the column's contents (Dhanur, 27 Oct).
  return Math.round(
    Math.min(
      LEFT_COLUMN_MAX_WIDTH_PX,
      Math.max(
        LEFT_COLUMN_MIN_WIDTH_PX,
        viewportWidth * LEFT_COLUMN_VIEWPORT_RATIO
      )
    )
  )
}

// The detections rail's width for `expandedColumns` open lanes, the rest
// collapsed to their spines, capped by the room available.
const railWidthForColumns = (availableWidth: number, expandedColumns: number) =>
  Math.min(
    availableWidth,
    expandedColumns * DETECTION_COLUMN_MAX_WIDTH_PX +
      (DETECTION_COLUMN_COUNT - expandedColumns) * DETECTION_SPINE_WIDTH_PX
  )

// How many feed lanes fit beside the File at this viewport, the rail width
// the chosen view mode renders at, and its width collapsed to its spines.
const getFeedRailLayout = ({
  viewportWidth,
  companyRailWidth,
  feedViewMode,
}: {
  viewportWidth: number
  companyRailWidth: number
  feedViewMode: FeedViewMode
}) => {
  const isWide = viewportWidth >= TWO_XL_WIDTH_PX
  // Four shell gutters (--terminal-shell-gutter) cross the row: the browser's
  // left edge, list to File, File to rail, rail to the right edge.
  const shellGutter = isWide ? 12 : 8
  const availableWidth =
    viewportWidth -
    companyRailWidth -
    4 * shellGutter -
    (isWide ? FILE_WIDE_MIN_WIDTH_PX : FILE_MIN_WIDTH_PX)
  const maxExpandedColumns =
    Array.from(
      { length: DETECTION_COLUMN_COUNT },
      (_, index) => DETECTION_COLUMN_COUNT - index
    ).find(
      (expanded) =>
        availableWidth >=
        expanded * DETECTION_COLUMN_MIN_WIDTH_PX +
          (DETECTION_COLUMN_COUNT - expanded) * DETECTION_SPINE_WIDTH_PX
    ) ?? 0
  const renderedFeedViewMode =
    maxExpandedColumns === 0 ? FeedViewMode.COLLAPSED : feedViewMode
  const renderedExpandedColumns =
    renderedFeedViewMode === FeedViewMode.FOCUSED ? 1 : maxExpandedColumns
  const collapsedRailWidth = DETECTION_SPINE_WIDTH_PX * DETECTION_COLUMN_COUNT
  return {
    renderedFeedViewMode,
    renderedExpandedColumns,
    feedRailWidth:
      renderedFeedViewMode === FeedViewMode.COLLAPSED
        ? collapsedRailWidth
        : railWidthForColumns(availableWidth, renderedExpandedColumns),
    // The centre well with the rail collapsed to its spines: the width every
    // Portfolio view lays itself out for (the inspection width). A fixed
    // number, so it holds while the rail animates its width.
    inspectionWidth:
      viewportWidth - companyRailWidth - 4 * shellGutter - collapsedRailWidth,
  }
}

// SSR and the first client render agree on 2xl; the real viewport takes over
// after hydration.
const useViewportWidth = () => {
  const [viewportWidth, setViewportWidth] = useState(TWO_XL_WIDTH_PX)
  useEffect(() => {
    const updateWidth = () => setViewportWidth(window.innerWidth)
    updateWidth()
    window.addEventListener("resize", updateWidth)
    return () => window.removeEventListener("resize", updateWidth)
  }, [])
  return viewportWidth
}

const TerminalGateway = () => {
  const { hasTerminalAccess } = Route.useRouteContext()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  if (hasTerminalAccess) return <TerminalShell />

  return (
    <Suspense fallback={null}>
      <TerminalWebsitePage
        routeState={{ q: search.q, term: search.term }}
        onRouteStateChange={(routeState, options) => {
          void navigate({ search: { ...search, ...routeState }, ...options })
        }}
      />
    </Suspense>
  )
}

export const Route = createFileRoute("/_terminal/terminal")({
  validateSearch: (search) => terminalSearchSchema.parse(search),
  // The Portfolio rows and feed change only when a Company Run ends;
  // mutations refresh them through router.invalidate().
  staleTime: 30 * 60_000,
  preloadStaleTime: 30 * 60_000,
  beforeLoad: async ({ context, search }) => {
    if (!context.hasTerminalAccess || search.portfolio) return
    const portfolio = defaultPortfolio(await loadTerminalPortfolios())
    if (!portfolio) return
    throw redirect({
      to: ".",
      search: { ...search, portfolio: portfolio.slug },
    })
  },
  loaderDeps: ({ search }) => ({ portfolio: search.portfolio }),
  loader: async ({ context, deps }) => {
    const empty = {
      portfolios: [],
      rows: [],
      feed: [],
      fileRequests: [],
      detectionFeedback: null,
    }
    if (!context.hasTerminalAccess || !deps.portfolio) return empty
    const portfolioSlug = deps.portfolio
    const [portfolios, rows, feed, fileRequests, detectionFeedback] =
      await Promise.allSettled([
        loadTerminalPortfolios(),
        loadTerminalPortfolio({ data: { portfolioSlug } }),
        loadTerminalFeed({ data: { portfolioSlug } }),
        loadTerminalFileRequests({ data: { portfolioSlug } }),
        loadTerminalDetectionFeedback({ data: { portfolioSlug } }),
      ])
    if (portfolios.status === "rejected") throw portfolios.reason
    // A stale or mistyped link names a Portfolio the user cannot open: send
    // them to their default one, or to the empty state when they have none.
    if (!portfolios.value.some(({ slug }) => slug === portfolioSlug)) {
      const portfolio = defaultPortfolio(portfolios.value)
      if (portfolio)
        throw redirect({
          to: "/terminal",
          search: { portfolio: portfolio.slug },
        })
      return empty
    }
    if (rows.status === "rejected") throw rows.reason
    if (feed.status === "rejected") throw feed.reason
    if (fileRequests.status === "rejected") throw fileRequests.reason
    return {
      portfolios: portfolios.value,
      rows: rows.value,
      feed: feed.value,
      fileRequests: fileRequests.value,
      // The vote is optional: if the answers cannot be read, the rail shows
      // the Detections without it.
      detectionFeedback:
        detectionFeedback.status === "fulfilled"
          ? detectionFeedback.value
          : null,
    }
  },
  // After the loader: an inline `head` placed earlier breaks the route's type
  // inference.
  head: ({ match }) => ({
    links: match.context.hasTerminalAccess
      ? []
      : [{ rel: "stylesheet", href: terminalWebsiteStyles }],
  }),
  component: TerminalGateway,
})

// `[` toggles the company list (File only), `]` toggles the feed, and Escape
// backs out of the current expansion or the File. Ignored while typing or with
// a modifier held.
const useTerminalShellKeyboard = ({
  isHomeRef,
  isSidebarCollapsedRef,
  isFeedCollapsedRef,
  feedViewModeRef,
  setFeedViewMode,
  onSidebarCollapsedChange,
  onFeedCollapsedChange,
  onNavigateHome,
}: {
  isHomeRef: RefObject<boolean>
  isSidebarCollapsedRef: RefObject<boolean>
  isFeedCollapsedRef: RefObject<boolean>
  feedViewModeRef: RefObject<FeedViewMode>
  setFeedViewMode: (mode: FeedViewMode) => void
  onSidebarCollapsedChange: (isCollapsed: boolean) => void
  onFeedCollapsedChange: (isCollapsed: boolean) => void
  onNavigateHome: () => void
}) => {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const active = document.activeElement
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        (active instanceof HTMLElement && active.isContentEditable)
      )
        return
      if (event.key === "Escape") {
        if (
          document.querySelector(
            '[role="dialog"][aria-modal="true"], [data-radix-popper-content-wrapper]'
          )
        )
          return
        if (isHomeRef.current) {
          if (feedViewModeRef.current === FeedViewMode.COLLAPSED) return
          event.preventDefault()
          setFeedViewMode(FeedViewMode.COLLAPSED)
        } else if (isSidebarCollapsedRef.current) {
          event.preventDefault()
          onNavigateHome()
        } else {
          event.preventDefault()
          onSidebarCollapsedChange(true)
        }
        return
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (event.key === "[" && !isHomeRef.current) {
        event.preventDefault()
        onSidebarCollapsedChange(!isSidebarCollapsedRef.current)
      } else if (event.key === "]") {
        event.preventDefault()
        onFeedCollapsedChange(!isFeedCollapsedRef.current)
      }
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [
    isHomeRef,
    isSidebarCollapsedRef,
    isFeedCollapsedRef,
    feedViewModeRef,
    setFeedViewMode,
    onSidebarCollapsedChange,
    onFeedCollapsedChange,
    onNavigateHome,
  ])
}

const TerminalShell = () => {
  const { portfolios, rows, feed, fileRequests, detectionFeedback } =
    Route.useLoaderData()
  const { isSignedIn } = Route.useRouteContext()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const selectedSlug =
    useMatch({ from: "/_terminal/terminal/$slug", shouldThrow: false })?.params
      .slug ?? null
  // One Monitoring/One-time choice for both modes, held in the URL. An open
  // File decides it: the choice is that company's list, whichever link opened
  // the File.
  const selectedList = useMemo(
    () => listOfOpenFile(rows, selectedSlug),
    [rows, selectedSlug]
  )
  const urlPopulation = search.population ?? PortfolioCompanyStatus.PORTFOLIO
  const population = selectedList ?? urlPopulation
  useEffect(() => {
    if (selectedList === null || selectedList === urlPopulation) return
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, population: selectedList }),
      replace: true,
    })
  }, [navigate, urlPopulation, selectedList])
  // No File selected is Home: the dock on the left, the index well centered.
  // Requests opens in Home's center well, with nothing in the dock selected.
  const isHome = selectedSlug === null
  // Requests, Definitions and the Policy 2.0 page are standing pages in the
  // center well: nothing in the dock is selected and the detections rail
  // collapses, since none has a company for it to follow.
  const isStandingPageOpen = useMatches({ select: shellModeOf }) === "page"

  const [hoveredSlug, setHoveredSlug] = useState<string | null>(null)
  const [isCommandPaletteOpen, setCommandPaletteOpen] = useState(false)
  // The last File opened this session, so the header's Companies switch
  // returns there.
  const [lastViewedSlug, setLastViewedSlug] = useState<string | null>(null)
  // Session-local UI state lives in the shell so it survives the Home⇄File
  // crossing that unmounts the list and the feed. The list opens strongest
  // Assessment first.
  const [sort, setSort] = useState(CompanySortKey.ASSESSMENT)
  const [order, setOrder] = useState<"asc" | "desc">("desc")
  const [bands, setBands] = useState<AssessmentBand[]>([])
  const [feedWindow, setFeedWindow] = useState(TimeWindow.LIFETIME)
  const { theme: terminalTheme } = useTerminalTheme()
  const viewportWidth = useViewportWidth()

  // A Key Risk cell's click-through asks the feed to reveal its Detection.
  const detectionFocusTarget = useTerminalHomeStore(
    (state) => state.detectionFocusTarget
  )
  const {
    feedViewMode,
    isFeedCollapsed,
    isSidebarCollapsed,
    setFeedViewMode,
    handleSidebarCollapsedChange,
    handleFeedCollapsedChange,
  } = useTerminalRailLayout({ isHome, detectionFocusTarget })
  const companyRailWidth = getCompanyRailWidth({
    viewportWidth,
    isHome,
    isSidebarCollapsed,
  })
  const {
    renderedFeedViewMode,
    renderedExpandedColumns,
    feedRailWidth,
    inspectionWidth,
  } = getFeedRailLayout({ viewportWidth, companyRailWidth, feedViewMode })

  // Live values mirrored into refs so the keyboard listener binds once.
  const isHomeRef = useRef(isHome)
  isHomeRef.current = isHome
  const isSidebarCollapsedRef = useRef(isSidebarCollapsed)
  isSidebarCollapsedRef.current = isSidebarCollapsed
  const isFeedCollapsedRef = useRef(isFeedCollapsed)
  isFeedCollapsedRef.current = isFeedCollapsed
  const feedViewModeRef = useRef(feedViewMode)
  feedViewModeRef.current = feedViewMode

  const navigateHome = useCallback(() => {
    void navigate({ to: "/terminal", search: (prev) => ({ ...prev }) })
  }, [navigate])

  useTerminalShellKeyboard({
    isHomeRef,
    isSidebarCollapsedRef,
    isFeedCollapsedRef,
    feedViewModeRef,
    setFeedViewMode,
    onSidebarCollapsedChange: handleSidebarCollapsedChange,
    onFeedCollapsedChange: handleFeedCollapsedChange,
    onNavigateHome: navigateHome,
  })

  useEffect(() => {
    if (selectedSlug) setLastViewedSlug(selectedSlug)
  }, [selectedSlug])

  // Overlay-scrollbar reveal: tag the element that is scrolling with
  // `.is-scrolling` (terminal-theme.css fades its thumb in), then clear it
  // after an idle beat. Capture phase, because `scroll` does not bubble.
  useEffect(() => {
    const timerByElement = new WeakMap<Element, ReturnType<typeof setTimeout>>()
    const revealScrollbar = (event: Event) => {
      const { target } = event
      if (!(target instanceof Element)) return
      target.classList.add("is-scrolling")
      clearTimeout(timerByElement.get(target))
      timerByElement.set(
        target,
        setTimeout(() => target.classList.remove("is-scrolling"), 700)
      )
    }
    document.addEventListener("scroll", revealScrollbar, true)
    return () => document.removeEventListener("scroll", revealScrollbar, true)
  }, [])

  // A Detection click-through must land on a visible card, so a focus request
  // widens the feed window to LIFETIME.
  useEffect(() => {
    if (detectionFocusTarget) setFeedWindow(TimeWindow.LIFETIME)
  }, [detectionFocusTarget])

  const selectedRow = selectedSlug
    ? rows.find(({ company }) => company.slug === selectedSlug)
    : undefined

  const scopedFeed = useMemo(() => {
    if (selectedSlug)
      return feed.filter(({ company }) => company.slug === selectedSlug)
    if (bands.length === 0) return feed
    const bandSet = new Set(bands)
    const companyIds = new Set(
      rows
        .filter(
          ({ deliveredFile }) =>
            deliveredFile && bandSet.has(deliveredFile.assessment.band)
        )
        .map(({ company }) => company.id)
    )
    return feed.filter(({ company }) => companyIds.has(company.id))
  }, [feed, rows, selectedSlug, bands])

  const handleCompanyHover = useCallback(
    (slug: string | null) => setHoveredSlug(slug),
    []
  )

  const selectCompany = useCallback(
    (slug: string) => {
      void navigate({
        to: "/terminal/$slug",
        params: { slug },
        search: (prev) => ({ ...prev }),
      })
    },
    [navigate]
  )

  const companiesTargetSlug = useMemo(
    () => companiesTargetOf(rows, population, lastViewedSlug),
    [rows, population, lastViewedSlug]
  )

  // Portfolio search: the open view scrolls to the company and lights it, as
  // a jump in Requests does; a company the view does not hold opens its File.
  const locator = useTerminalHomeStore((s) => s.locator)
  const setHighlightedSlug = useTerminalHomeStore((s) => s.setHighlightedSlug)
  const [jumpSlug, jumpTo] = useJumpHighlight(findCompanyRow)
  useEffect(() => {
    setHighlightedSlug(jumpSlug)
  }, [jumpSlug, setHighlightedSlug])
  const findInView = (slug: string) => {
    if (!locator?.holds(slug)) return selectCompany(slug)
    locator.reveal(slug)
    jumpTo(slug)
  }

  const modeView: TerminalHomeView = isHome ? "home" : "companies"
  const headerView = isStandingPageOpen ? null : modeView
  const selectView = (view: TerminalHomeView) => {
    if (view === "home") navigateHome()
    else if (companiesTargetSlug) selectCompany(companiesTargetSlug)
  }

  return (
    <div
      data-terminal-theme={terminalTheme}
      className={cn(
        terminalTheme === "dark" && "dark",
        "bg-surface-chrome text-foreground flex h-[100dvh] flex-col overflow-hidden"
      )}
    >
      <TerminalHeader
        portfolios={portfolios}
        activePortfolioSlug={search.portfolio}
        // A standing page is neither mode, so the switch selects neither and
        // both sides lead out: Portfolio to the view left, Companies to the
        // last File (Dhanur, 27 Oct).
        activeView={headerView}
        onSelectView={selectView}
        areCompaniesDisabled={companiesTargetSlug === null}
        isSignedIn={isSignedIn}
        // Every request not yet Published counts, Needs clarification
        // included, as one number (Dhanur, 25 Oct).
        inProgressRequestCount={
          fileRequests.filter(
            ({ status }) => status !== FileRequestStatus.PUBLISHED
          ).length
        }
        rows={rows}
      />

      {/* The ⌘K wayfinding palette, summonable from Home or any File. */}
      <CommandPalette
        rows={rows}
        // On the Portfolio, search finds the company in the open view; in
        // Companies, it opens the File.
        onSelect={isHome ? findInView : selectCompany}
        isOpen={isCommandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
      />

      {search.portfolio ? (
        <div className="flex min-h-0 flex-1">
          {/* A page has only the header: the left column leaves (Screens ›
              Page frame). */}
          <aside
            data-terminal-rail
            className={cn(
              "bg-surface-chrome flex min-h-0 shrink-0 flex-col overflow-hidden pl-(--terminal-shell-gutter) transition-[width] duration-(--detection-swap-fade) ease-(--detection-swap-ease)",
              isStandingPageOpen && "hidden"
            )}
            // The column sits inside the shell gutter; the File gives up the
            // width.
            style={{
              width: `calc(${companyRailWidth}px + var(--terminal-shell-gutter))`,
            }}
          >
            <div
              id="terminal-company-roster"
              className="flex-1 overflow-hidden"
            >
              {isHome ? (
                <HomeDock
                  onOpenSearch={() => setCommandPaletteOpen(true)}
                  rows={rows}
                  feed={feed}
                  population={population}
                  selectedView={search.view ?? null}
                  selectedReport={search.report ?? null}
                  isSelectionCleared={isStandingPageOpen}
                  onPopulationChange={(nextPopulation) =>
                    void navigate({
                      to: "/terminal",
                      search: (prev) => ({
                        ...prev,
                        population: nextPopulation,
                      }),
                    })
                  }
                  onSelectView={(view) =>
                    void navigate({
                      to: "/terminal",
                      search: (prev) => ({
                        ...prev,
                        view,
                        report: undefined,
                      }),
                    })
                  }
                  onSelectReport={(report) =>
                    void navigate({
                      to: "/terminal",
                      search: (prev) => ({
                        ...prev,
                        view: undefined,
                        report,
                      }),
                    })
                  }
                />
              ) : (
                <CompanyList
                  rows={rows}
                  search={{ ...search, population }}
                  sort={sort}
                  order={order}
                  onSortChange={(nextSort, nextOrder) => {
                    setSort(nextSort)
                    setOrder(nextOrder)
                  }}
                  bands={bands}
                  onBandsChange={setBands}
                  selectedSlug={selectedSlug}
                  hoveredSlug={hoveredSlug}
                  onCompanyHover={handleCompanyHover}
                  onOpenCommandPalette={() => setCommandPaletteOpen(true)}
                  isCollapsed={isSidebarCollapsed}
                  onCollapsedChange={handleSidebarCollapsedChange}
                />
              )}
            </div>
          </aside>

          <div className="flex min-h-0 min-w-0 flex-1 justify-center gap-(--terminal-shell-gutter) p-(--terminal-shell-gutter)">
            {/* A page narrows the well to the page width and centres it
                (Screens › Page frame); crossing into or out of a page, the
                well is the one thing that moves (`terminal-well`,
                terminal-theme.css). */}
            <main
              data-terminal-well
              className="bg-background screen-glow relative block min-h-0 min-w-0 flex-1 overflow-hidden rounded-lg"
              // Portfolio inspection width (Dhanur, 26 Oct 2026): a
              // Portfolio view lays itself out for the centre width with the
              // Detections rail collapsed, and keeps that layout when the
              // rail opens, scrolling horizontally instead of recomposing.
              // The shell knows that width; the ViewFrame reads it.
              style={{
                maxWidth: isStandingPageOpen
                  ? "var(--terminal-page-width)"
                  : "100%",
                ...(isHome &&
                  !isStandingPageOpen && {
                    "--portfolio-inspection-width": `${inspectionWidth}px`,
                  }),
              }}
            >
              {/* The index route is the Home well; $slug is the File. */}
              <Outlet />
              <ScreenGrain />
            </main>

            {/* A page has no company for the rail to follow: the rail leaves
                with the left column (Screens › Page frame). */}
            <aside
              id="terminal-detections-feed"
              className={cn(
                "bg-background screen-glow border-border-faint relative block min-h-0 shrink-0 overflow-hidden rounded-lg border transition-[width] duration-(--detection-swap-fade) ease-(--detection-swap-ease)",
                isStandingPageOpen && "hidden"
              )}
              style={{ width: feedRailWidth }}
            >
              <DetectionFeedbackProvider
                key={search.portfolio}
                portfolioSlug={search.portfolio}
                initialAnswers={detectionFeedback}
              >
                <FeedPane
                  feedItems={scopedFeed}
                  scopedCompanyName={selectedRow?.company.name ?? null}
                  window={feedWindow}
                  onWindowChange={setFeedWindow}
                  onHoverCompany={handleCompanyHover}
                  onCompanyClick={selectCompany}
                  mode={renderedFeedViewMode}
                  onModeChange={setFeedViewMode}
                  maxExpandedColumns={Math.max(1, renderedExpandedColumns)}
                  focusTarget={detectionFocusTarget}
                />
              </DetectionFeedbackProvider>
              <ScreenGrain />
            </aside>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 items-center justify-center">
          <TerminalEmptyState>
            No Portfolio is shared with this account.
          </TerminalEmptyState>
        </div>
      )}
    </div>
  )
}
